import { identifier } from 'app/lib/utils/sql_generator.js';
import { DATA_TYPE_MAP, tableAlias } from './utils.js';

const translateQuotes = (str) => {
    const content = JSON.parse(str);
    const escapedContent = content.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

    return "'" + escapedContent + "'";
};

export const translateSimpleReference = (columns) => (node) => {
    const columnName = node.get('ColumnName');

    if (!columnName) {
        throw Error('simple reference missing column name');
    }

    const column = columns.find((column) => columnName.value === column.name);

    return { exp: identifier(columnName.value), type: DATA_TYPE_MAP[column?.type._type] };
};

export const translateSheetReference = (sources) => (node) => {
    const columnName = node.get('ColumnName');
    const sheetName = node.get('SheetName');

    if (!columnName) {
        throw Error('sheet reference missing column name');
    }

    const sheet = sources.find((source) => source.suggestedName === sheetName.value);
    const column = sheet?.columns.find((column) => column.name === columnName.value);

    return {
        exp: `Source.${identifier(columnName.value)}`,
        type: DATA_TYPE_MAP[column?.type._type]
    };
};

export const translateLiteral = (node) => ({ exp: node.value });
export const translateDoubleQuoteString = (node) => ({ exp: translateQuotes(node.value) });
export const translateOp = (node) => ({ exp: ` ${node.value} ` });
export const translateParens = (node) => ({ exp: `${node.value}` });

const getArgs = (node) => node.get('FunctionArgs')?.children;

const translateCase = (converter) => (node) => {
    const args = getArgs(node);

    if (!args || args.length < 3) {
        throw Error('Invalid case function');
    }

    const defaultValue = converter.translate(args[0]);

    if (args.length % 2 === 0) {
        const test = converter.translate(args[1]);
        const cases = [];

        for (let i = 2; i < args.length; i += 2) {
            const when = converter.translate(args[i]);
            const then = converter.translate(args[i + 1]);

            cases.push(`WHEN ${when.exp} THEN ${then.exp}`);
        }

        return { exp: `CASE ${test.exp} ${cases.join(' ')} ELSE ${defaultValue.exp} END` };
    }

    const cases = [];

    for (let i = 1; i < args.length; i += 2) {
        const when = converter.translate(args[i]);
        const then = converter.translate(args[i + 1]);

        cases.push(`WHEN ${when.exp} THEN ${then.exp}`);
    }

    return { exp: `CASE ${cases.join(' ')} ELSE ${defaultValue.exp} END` };
};

export const translateTernaryOp = (converter) => (node) => {
    const testExp = converter.translate(node.get('TestExpression'));
    const thenExp = converter.translate(node.get('ThenExpression'));
    const elseExp = converter.translate(node.get('ElseExpression'));

    if (!testExp || !thenExp || !elseExp) {
        throw Error('incomplete ternary expression');
    }

    return { exp: `IFF(${testExp.exp}, ${thenExp.exp}, ${elseExp.exp})` };
};

const translateIsBlank = (converter) => (node) => {
    const args = getArgs(node);

    if (args?.length !== 1) {
        throw Error('isblank should have exactly 1 argument');
    }

    const trimArg = converter.translate(args[0]);

    return { exp: `COALESCE(TRIM(${trimArg.exp}), '') <> ''` };
};

const translateIsNull = (converter) => (node) => {
    const args = getArgs(node);

    if (args?.length !== 1) {
        throw Error('isnull should have exactly 1 argument');
    }

    return { exp: `${converter.translate(args[0]).exp} IS NULL` };
};

const translateDenullify = (converter) => (node) => {
    const args = getArgs(node);

    if (args?.length !== 1 && args?.length !== 2) {
        throw Error('denullify only accepts 1 or 2 arguments');
    }

    if (args.length === 2) {
        return {
            exp: `COALESCE(${converter.translate(args[0]).exp}, ${converter.translate(args[1]).exp})`
        };
    }

    const arg = converter.translate(args[0]);

    switch (arg.type) {
        case 'STRING':
            return { ...arg, exp: `COALESCE(${arg.exp}, '')` };
        case 'INTEGER':
        case 'DOUBLE':
        case 'BOOLEAN':
            return { ...arg, exp: `COALESCE(${arg.exp}, 0)` };
        case 'DATE':
            return { ...arg, exp: `COALESCE(${arg.exp}, DATE_FROM_PARTS(1970, 1, 1))` };
        default:
            return { ...arg, exp: arg.exp };
    }
};

const translateAnd = (converter) => (node) => {
    const args = getArgs(node);

    if (!args?.length || args.length < 2) {
        throw Error('and should have at least 2 arguments');
    }

    return {
        exp: args
            .map((arg) => converter.translate(arg))
            .map((arg) => arg.exp)
            .join(' AND ')
    };
};

const translateCopy = (converter) => (node) => {
    const args = getArgs(node);

    if (args?.length !== 1) {
        throw Error('copy should have exactly 1 argument');
    }

    return { exp: `${converter.translate(args[0]).exp}` };
};

const translateDiv = (converter) => (node) => {
    const args = getArgs(node);

    if (args?.length !== 2) {
        throw Error('div should have exactly 2 arguments');
    }

    return { exp: `${converter.translate(args[0]).exp} / ${converter.translate(args[1]).exp}` };
};

const translateE = () => ({ exp: `EXP(1)` });

const translateEquals = (converter) => (node) => {
    const args = getArgs(node);

    if (args?.length !== 2) {
        throw Error('equals should have exactly 2 arguments');
    }

    return { exp: `${converter.translate(args[0]).exp} = ${converter.translate(args[1]).exp}` };
};

const translateExpand = (converter) => (node) => {
    const args = getArgs(node);

    if (args?.length !== 1) {
        throw Error('expand should only have 1 argument');
    }

    const arg = converter.translate(args[0]);

    if (arg.tableExp) {
        throw Error('nested table expressions currently not supported');
    }

    const alias = tableAlias();

    return {
        exp: `${alias}.VALUE`,
        tableExp: `LATERAL FLATTEN(input => ${arg.exp}) ${alias}`
    };
};

const translateTokenize = (converter) => (node) => {
    const args = getArgs(node);

    if (args?.length !== 2 && args?.length !== 3) {
        throw Error('tokenize should have either 2 or 3 arguments');
    }

    if (args.length === 3) {
        const splitArgs = args.map((arg) => converter.translate(arg));

        return { exp: `SPLIT_PART(${splitArgs[0].exp}, ${splitArgs[1].exp}, ${splitArgs[2].exp})` };
    }

    const arg = converter.translate(args[0]);

    if (arg.tableExp) {
        throw Error('nested table expressions currently not supported');
    }

    const alias = tableAlias();

    return {
        exp: `${alias}.VALUE`,
        tableExp: `LATERAL SPLIT_TO_TABLE(${arg.exp}, ',') ${alias}`
    };
};

const translateGroupBy = (converter) => (node) => {
    const args = getArgs(node);

    if (args?.length !== 1) {
        throw Error('groupby should only have 1 argument');
    }

    const arg = converter.translate(args[0]);

    return {
        ...arg,
        exp: arg.exp,
        group: true
    };
};

const translateAggregateFunction = (converter, fn) => (node) => {
    const args = getArgs(node);

    if (args?.length !== 1) {
        throw Error('aggregate function should only have 1 argument');
    }

    const arg = converter.translate(args[0]);

    return {
        exp: `${fn}(${arg.exp})`,
        agg: true
    };
};

const translateGroupCount = () => ({ exp: 'COUNT(*)', agg: true });

const functionMap = {
    ABS: 'ABS',
    ACOS: 'ACOS',
    ACOSH: 'ACOSH',
    ASDATE: 'TO_DATE',
    ASIN: 'ASIN',
    ASINH: 'ASINH',
    ATAN: 'ATAN',
    ATAN2: 'ATAN2',
    ATANH: 'ATANH',
    CEILING: 'CEIL',
    CHAR: 'CHAR',
    COALESCE: 'COALESCE',
    CODE: 'ASCII',
    CONCAT: 'CONCAT',
    COS: 'COS',
    COSH: 'COSH',
    COT: 'COT',
    DAY: 'DAY',
    DAYOFYEAR: 'DAYOFYEAR',
    DEGREES: 'DEGREES',
    ENDSWITH: 'ENDSWITH',
    EXP: 'EXP',
    FLOOR: 'FLOOR',
    IF: 'IFF',
    INDEX: 'REGEXP_INSTR',
    LEFT: 'LEFT',
    LEN: 'LEN',
    LN: 'LN',
    LOG: 'LOG',
    MONTH: 'MONTH',
    NOW: 'CURRENT_TIMESTAMP',
    REPLACEALL: 'REPLACE',
    ROUND: 'ROUND',
    ROUNDDOWN: 'FLOOR',
    ROUNDUP: 'CEIL',
    SIN: 'SIN',
    SINH: 'SINH',
    SUBSTR: 'SUBSTR',
    T: 'TO_CHAR',
    TAN: 'TAN',
    TANH: 'TANH',
    TODAY: 'CURRENT_DATE',
    TOKENIZELIST: 'SPLIT',
    YEAR: 'YEAR'
};

export const translateFunction = (converter) => {
    const functionHelpers = {
        AND: translateAnd(converter),
        CASE: translateCase(converter),
        COPY: translateCopy(converter),
        DENULLIFY: translateDenullify(converter),
        DIV: translateDiv(converter),
        E: translateE,
        EQUALS: translateEquals(converter),
        ISBLANK: translateIsBlank(converter),
        ISNULL: translateIsNull(converter),
        EXPAND: translateExpand(converter),
        TOKENIZE: translateTokenize(converter),
        GROUPBY: translateGroupBy(converter),
        GROUPSUM: translateAggregateFunction(converter, 'SUM'),
        GROUPMAX: translateAggregateFunction(converter, 'MAX'),
        GROUPAVERAGE: translateAggregateFunction(converter, 'AVG'),
        GROUPCOUNT: translateGroupCount
    };

    return (node) => {
        const name = node.get('FunctionName')?.value.toUpperCase();

        if (functionHelpers[name]) {
            return functionHelpers[name](node);
        }

        const sqlName = functionMap[name];

        if (!sqlName) {
            throw Error(`Unknown function: ${name}`);
        }

        const args = getArgs(node)?.map((arg) => converter.translate(arg));
        const tableExp = args?.find((item) => item.tableExp)?.tableExp;
        const type = args?.find((item) => item.type)?.type;
        const group = args?.find((item) => item.group)?.group;
        const agg = args?.find((item) => item.agg)?.agg;

        return {
            exp: `${sqlName}(${args?.map((a) => a.exp).join(', ') ?? ''})`,
            tableExp,
            type,
            group,
            agg
        };
    };
};
